import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Box, useTheme, IconButton, Tooltip } from '@ublend-npm/aulaui-core';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWidgetPreferences } from '../../../actions/home/widgetPreferences';
import applyPendingHomeSpaceReorder from '../../../actions/home/applyPendingHomeSpaceReorder';
import { WidgetPreference } from '@core/types/state';
import { THEME_SWITCHING } from '@core/constants/actions';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import TourOutlinedIcon from '@mui/icons-material/TourOutlined';
import HomeWidgetLayout from './layout/HomeWidgetLayout';
import { location as getLocation } from '@app/selectors/routing';
import { getHomeViewRole } from '@app/selectors/home';
import {
  HomeTourProvider,
  HomeTourWelcome,
  useHomeTour,
  useHomeTourContext,
} from './tour';
import { getHomeTourWelcomeCopy } from './tour/homeTourSteps';
import { HOME_TOUR_FAB } from './tour/homeTourContent';
import { isDev } from '@app/utils/environment';

const FAB_SIZE = 48;
const FAB_RIGHT = 24;
const FAB_GAP = 16;
/**
 * Bottom-right FAB stack (bottom → top):
 * 1. Zendesk help launcher (rendered by Zendesk widget, ~bottom:0 — see Zendesk.style.css)
 * 2. Tour FAB — TOUR_FAB_BOTTOM clears the Zendesk bubble
 * 3. Theme FAB — THEME_FAB_BOTTOM = tour bottom + FAB_SIZE + FAB_GAP (dev only)
 *
 * Order is decided only by these `bottom` offsets — not DOM order.
 */
const TOUR_FAB_BOTTOM = 80;
const THEME_FAB_BOTTOM = TOUR_FAB_BOTTOM + FAB_SIZE + FAB_GAP;

const fabBaseSx = {
  position: 'fixed' as const,
  right: `${FAB_RIGHT}px`,
  width: `${FAB_SIZE}px`,
  height: `${FAB_SIZE}px`,
  borderRadius: '50%',
  zIndex: 1000,
  boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.25)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:active': {
    transform: 'scale(0.95)',
  },
};

const ThemeToggleFAB = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDarkMode = theme.palette.mode === 'dark';

  if (!isDev()) {
    return null;
  }

  const handleToggle = () => {
    const nextTheme = isDarkMode ? 'light' : 'dark';
    localStorage.setItem('theme', nextTheme);
    dispatch({ type: THEME_SWITCHING, theme: nextTheme });
  };

  return (
    <IconButton
      onClick={handleToggle}
      aria-label="Toggle dark/light theme"
      sx={{
        ...fabBaseSx,
        bottom: `${THEME_FAB_BOTTOM}px`,
        backgroundColor: isDarkMode ? '#ffb74d' : '#1a237e',
        color: isDarkMode ? '#1a237e' : '#fff',
        '&:hover': {
          backgroundColor: isDarkMode ? '#ffa726' : '#0d1b60',
          transform: 'scale(1.1) rotate(360deg)',
          boxShadow: isDarkMode
            ? '0px 8px 24px rgba(255, 183, 77, 0.4)'
            : '0px 8px 24px rgba(26, 35, 126, 0.4)',
        },
      }}
    >
      {isDarkMode ? (
        <LightModeIcon
          sx={{ fontSize: '28px', transition: 'transform 0.4s' }}
        />
      ) : (
        <DarkModeIcon sx={{ fontSize: '28px', transition: 'transform 0.4s' }} />
      )}
    </IconButton>
  );
};

const HomeTourFAB = () => {
  const theme = useTheme();
  const homeTour = useHomeTourContext();
  const isDarkMode = theme.palette.mode === 'dark';

  if (!homeTour) {
    return null;
  }

  const handleClick = () => {
    homeTour.startTour({ force: true, source: 'replay' });
  };

  return (
    <Tooltip title={HOME_TOUR_FAB.tooltip} placement="left" arrow>
      {/* Fixed positioning must be on Tooltip's child so hover hits the visible FAB */}
      <Box
        component="span"
        sx={{
          ...fabBaseSx,
          bottom: `${TOUR_FAB_BOTTOM}px`,
        }}
      >
        <IconButton
          onClick={handleClick}
          disabled={homeTour.isTourActive}
          aria-label={HOME_TOUR_FAB.ariaLabel}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            padding: 0,
            backgroundColor: isDarkMode ? '#7c5cbf' : '#513093',
            color: '#fff',
            opacity: homeTour.isTourActive ? 0.6 : 1,
            '&:hover': {
              backgroundColor: isDarkMode ? '#9170d4' : '#3f2474',
              transform: homeTour.isTourActive ? 'none' : 'scale(1.1)',
              boxShadow: isDarkMode
                ? '0px 8px 24px rgba(124, 92, 191, 0.45)'
                : '0px 8px 24px rgba(81, 48, 147, 0.4)',
            },
            '&.Mui-disabled': {
              backgroundColor: isDarkMode ? '#7c5cbf' : '#513093',
              color: '#fff',
              opacity: 0.6,
            },
          }}
        >
          <TourOutlinedIcon sx={{ fontSize: '26px' }} />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

const HomePage = () => {
  const dispatch = useDispatch();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pathname = useSelector(getLocation)?.pathname ?? '';

  const widgetPreferences: WidgetPreference[] | null = useSelector(
    (state: any) => state.home?.widgetPreferences
  );
  const viewRole = useSelector(getHomeViewRole);
  const welcomeCopy = getHomeTourWelcomeCopy(viewRole);

  const tour = useHomeTour({ isReady: Boolean(widgetPreferences) });

  useLayoutEffect(() => {
    const scrollNode = scrollRef.current;
    if (!scrollNode) {
      return undefined;
    }

    const syncScrollportHeight = () => {
      scrollNode.style.setProperty(
        '--home-scrollport-height',
        `${scrollNode.clientHeight}px`
      );
    };

    syncScrollportHeight();
    const resizeObserver = new ResizeObserver(syncScrollportHeight);
    resizeObserver.observe(scrollNode);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    document.title = 'Home - Aula';
    if (!widgetPreferences) {
      dispatch(fetchWidgetPreferences() as any);
    }
  }, [dispatch, widgetPreferences]);

  useEffect(() => {
    if (pathname.includes('/home')) {
      dispatch(applyPendingHomeSpaceReorder());
    }
  }, [dispatch, pathname]);

  return (
    <HomeTourProvider value={tour}>
      <Box
        ref={scrollRef}
        data-home-scrollport
        display="flex"
        flexDirection="column"
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          height: '100%',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          // Keep vertical scroll contained; allow horizontal 2-finger browser
          // back/forward everywhere except the space carousel (handled there).
          overscrollBehaviorY: 'contain',
          overflowAnchor: 'none',
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        <HomeWidgetLayout widgetPreferences={widgetPreferences} />
        <HomeTourWelcome
          open={tour.showWelcome}
          title={welcomeCopy.title}
          description={welcomeCopy.description}
          onStart={tour.onWelcomeStart}
          onDismiss={tour.onWelcomeDismiss}
        />
        <HomeTourFAB />
        <ThemeToggleFAB />
      </Box>
    </HomeTourProvider>
  );
};

export default HomePage;
